/*
 * CUSTOM FOR GO — <go-products-bundle>
 * Companion JS for sections/go-products-bundle.liquid.
 *
 * Two modes, chosen by the "Combine with Treasure Vault" toggle (data-combine):
 *
 *  STANDARD (combine = false) — unchanged from the original:
 *   - regular / discounted / savings total from the selected variant of each row
 *   - the discount code is a fixed EUR amount (data-discount-cents) off the
 *     bundle total, capped at the total.
 *   - "Add all to cart" adds the items and auto-applies the discount code via a
 *     non-navigating fetch('/discount/CODE') (applied BEFORE the add so Rebuy's
 *     drawer, which can open off the /cart/add interceptor, already sees it).
 *
 *  COMBINE (combine = true) — mirror the Rebuy bundle builder ("Treasure Vault"):
 *   - reads the tier rules (data-tiers, cents) and shows each item's tiered
 *     line-item price, the tiered total, our code on top, and total saved.
 *   - "Add all to cart" adds the items via /cart/add WITH the same line-item
 *     properties Rebuy's bundle builder sets (_source / _widget_id /
 *     _attribution / _r_added_bundle_session_id / _r_bbsi). Rebuy's Shopify
 *     Function groups line items by _r_added_bundle_session_id, sums them to
 *     find the tier, and applies the per-line discount server-side — so the
 *     discount works without driving Rebuy's (undocumented) widget API and on
 *     any page. Our influencer code is applied on top as usual.
 *
 * The per-row price swap on variant change is still handled by the native
 * <product-bundle-variant-selector> element (theme.js), which we reuse as-is.
 */
class GoProductsBundle extends HTMLElement {
  constructor() {
    super();

    this.cartDrawerElement = document.querySelector('cart-drawer');
    this.addToCartButtonElement = this.querySelector('button');
    // Fixed EUR amount (in cents) our code takes off the bundle total.
    this.discountCents = parseInt(this.dataset.discountCents, 10) || 0;
    this.discountCode = (this.dataset.discountCode || '').trim();

    // Combine (Treasure Vault) config
    this.combine = this.dataset.combine === 'true';
    try {
      this.tiers = JSON.parse(this.dataset.tiers || '[]');
    } catch (e) {
      this.tiers = [];
    }
    this.rebuyWidgetId = (this.dataset.rebuyWidgetId || '').trim();
    this.hideEmptyBundleBar = this.dataset.hideEmptyBundleBar === 'true';
    this.preapplyOnLoad = this.dataset.preapplyOnLoad === 'true';

    this.regularEl = this.querySelector('[data-bundle-regular]');
    this.discountedEl = this.querySelector('[data-bundle-discounted]');
    this.savingsEl = this.querySelector('[data-bundle-savings]');
    this.vaultEl = this.querySelector('[data-bundle-vault]');
    this.vaultPctEl = this.querySelector('[data-bundle-vault-pct]');

    this.addToCartButtonElement &&
      this.addToCartButtonElement.addEventListener('click', this.handleButtonClick.bind(this));

    // Recompute the bundle total whenever any row's variant changes.
    this.addEventListener('change', (event) => {
      if (event.target && event.target.matches('select[name="id"]')) this.updateTotals();
    });

    // Initial sync (fills per-item vault prices on load in combine mode).
    this.updateTotals();

    // Optionally collapse the Rebuy bundle builder bar while it's empty.
    this.initBundleBarToggle();

    // Optionally credit the discount code as soon as the landing page loads.
    this.preapplyDiscountOnLoad();
  }

  /* ----- Pre-apply the discount code on page load ----- */
  preapplyDiscountOnLoad() {
    if (!this.preapplyOnLoad || !this.discountCode) return;
    // Re-apply on EVERY landing-page load (no session guard) so the code is
    // restored even if the visitor removed it at checkout and came back. The
    // section only exists on the landing page, so this fires only there.
    // Redirect to /cart.js (tiny JSON) instead of the homepage so the session
    // discount is set with a minimal background request.
    fetch(`/discount/${encodeURIComponent(this.discountCode)}?redirect=/cart.js`, {
      method: 'GET',
      redirect: 'follow',
    }).catch((e) => console.error('[GO Bundle] Pre-apply discount failed', e));
  }

  /* ----- Hide the Rebuy bundle builder bar until it has a product ----- */
  getBundleSelectedCount() {
    const el = document.querySelector('.rebuy-bundle-builder__group-container-header-products_count');
    if (!el) return 0;
    const n = parseInt(el.textContent.trim(), 10);
    return Number.isNaN(n) ? 0 : n;
  }

  initBundleBarToggle() {
    if (!this.hideEmptyBundleBar) return;

    // Hide up front so the bar never flashes before it's measured; the sync
    // reveals it once the builder reports a selected product.
    document.body.classList.add('go-bundle-bar-hidden');

    const sync = () => {
      document.body.classList.toggle('go-bundle-bar-hidden', this.getBundleSelectedCount() === 0);
    };

    // Debounce: the builder re-renders a lot, and we observe a broad subtree
    // to catch the count text changing.
    let scheduled = false;
    const schedule = () => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        sync();
      });
    };

    new MutationObserver(schedule).observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    ['rebuy:cart.change', 'rebuy.productsChange', 'rebuy.ready', 'rebuy:smartcart.ready'].forEach((ev) =>
      document.addEventListener(ev, schedule)
    );

    sync();
  }

  /* ----- Selection helpers ----- */
  getSelectedOptions() {
    return [...this.querySelectorAll('select[name="id"]')]
      .map((select) => select.options[select.selectedIndex])
      .filter(Boolean);
  }

  getRows() {
    return [...this.querySelectorAll('product-bundle-variant-selector')]
      .map((el) => {
        const select = el.querySelector('select[name="id"]');
        const option = select && select.options[select.selectedIndex];
        return { el, option, vaultEl: el.querySelector('[data-vault-price]') };
      })
      .filter((row) => row.option);
  }

  tierFor(regularCents) {
    let pct = 0;
    this.tiers.forEach((t) => {
      if (regularCents >= t.min && t.pct > pct) pct = t.pct;
    });
    return pct;
  }

  /* ----- Totals ----- */
  updateTotals() {
    if (!this.regularEl) return;
    if (typeof FoxTheme === 'undefined' || !FoxTheme.Currency) return;
    const format = (cents) => FoxTheme.Currency.formatMoney(cents, FoxTheme.settings.moneyFormat);

    const regular = this.getSelectedOptions().reduce(
      (sum, option) => sum + (parseInt(option.dataset.price, 10) || 0),
      0
    );

    if (this.combine) {
      const tierPct = this.tierFor(regular);

      // Per-item Treasure Vault price
      this.getRows().forEach(({ el, option, vaultEl }) => {
        if (!vaultEl) return;
        const price = parseInt(option.dataset.price, 10) || 0;
        if (tierPct > 0) {
          vaultEl.innerHTML = format(Math.round(price * (1 - tierPct / 100)));
          vaultEl.hidden = false;
          el.classList.add('go-vault-active');
        } else {
          vaultEl.hidden = true;
          el.classList.remove('go-vault-active');
        }
      });

      const vaultTotal = Math.round(regular * (1 - tierPct / 100));
      const finalTotal = Math.max(0, vaultTotal - this.discountCents);
      const saved = regular - finalTotal;

      this.regularEl.innerHTML = format(regular);
      if (this.vaultEl) this.vaultEl.innerHTML = format(vaultTotal);
      if (this.vaultPctEl) this.vaultPctEl.textContent = tierPct;
      if (this.discountedEl) this.discountedEl.innerHTML = format(finalTotal);
      if (this.savingsEl) this.savingsEl.innerHTML = format(saved);
      return;
    }

    // Standard mode — fixed amount off, capped at the regular total
    if (this.discountCents <= 0) return;
    const savings = Math.min(this.discountCents, regular);
    const discounted = regular - savings;
    this.regularEl.innerHTML = format(regular);
    if (this.discountedEl) this.discountedEl.innerHTML = format(discounted);
    if (this.savingsEl) this.savingsEl.innerHTML = format(savings);
  }

  /* ----- Add all to cart ----- */
  async handleButtonClick(event) {
    event.preventDefault();
    this.lastClickedElement = event.submitter || event.currentTarget;

    const selectedOptions = this.getSelectedOptions().filter(
      (option) => option.dataset.available !== 'false' && !option.disabled
    );

    if (!selectedOptions.length) return;

    // Combine mode: tag every line with the Rebuy bundle-builder properties and
    // a shared session id, so Rebuy's Shopify Function applies the Treasure
    // Vault tier discount to them (grouped by _r_added_bundle_session_id).
    const bundleSessionId = this.combine ? this.makeBundleSessionId() : null;
    const items = {
      items: selectedOptions.map((option) => {
        const item = { id: option.value, quantity: 1 };
        if (this.combine) item.properties = this.rebuyBundleProperties(bundleSessionId);
        return item;
      }),
    };

    // Full page-load contexts (cart page / non-drawer): post then let the
    // discount URL carry the code through the redirect.
    if (document.body.classList.contains('cart-template') || FoxTheme.settings.cartType != 'drawer') {
      if (this.discountCode) {
        const ids = items.items.map((i) => `${i.id}:${i.quantity}`).join(',');
        window.location.href = `/discount/${encodeURIComponent(this.discountCode)}?redirect=/cart/add?items=${encodeURIComponent(ids)}`;
        return;
      }
      FoxTheme.utils.postLink(FoxTheme.routes.cart_add_url, { parameters: { ...items } });
      return;
    }

    this.showErrorMessage();
    this.toggleButtonLoading(true);

    // Apply the discount BEFORE adding to cart. Rebuy/BOGOS intercept the
    // /cart/add request and can open the drawer immediately, rendering the cart
    // snapshot at that instant. If the discount is applied afterwards it loses
    // the race on slower (mobile) connections — the drawer paints before the
    // /discount session is set, so the code only appears after a reload.
    // Setting it first guarantees the session already has the code when the
    // drawer opens, on mobile and desktop alike.
    await this.applyDiscount();

    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
    );

    const body = JSON.stringify({
      ...items,
      sections: sectionsToBundle,
      section_url: window.pathname,
    });

    fetch(`${FoxTheme.routes.cart_add_url}`, { ...FoxTheme.utils.fetchConfig('javascript'), ...{ body } })
      .then((response) => response.json())
      .then(async (parsedState) => {
        if (parsedState.status) {
          this.showErrorMessage(parsedState.description);
          return;
        }

        const cartJson = await (
          await fetch(`${FoxTheme.routes.cart_url}`, { ...FoxTheme.utils.fetchConfig() })
        ).json();
        cartJson['sections'] = parsedState['sections'];

        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
        document.dispatchEvent(
          new CustomEvent('product-ajax:added', { detail: { product: parsedState } })
        );
        this.cartDrawerElement && this.cartDrawerElement.show(this.lastClickedElement);
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        this.toggleButtonLoading(false);
      });
  }

  /* ----- Rebuy bundle properties (combine mode) ----- */
  // Mirrors the line-item properties Rebuy's bundle builder sets, so its Shopify
  // Function recognises these lines as a bundle and applies the tier discount.
  rebuyBundleProperties(sessionId) {
    return {
      _r_bbsi: '0',
      _r_added_bundle_session_id: sessionId,
      _source: 'Rebuy',
      _widget_id: this.rebuyWidgetId || '257102',
      _attribution: 'Rebuy Bundle Builder',
    };
  }

  // One shared UUID per "Add all" click groups the lines into a single bundle
  // (the Function tiers on the summed subtotal of items sharing this id).
  makeBundleSessionId() {
    try {
      if (window.crypto && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
    } catch (e) {
      /* fall through */
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  applyDiscount() {
    if (!this.discountCode) return Promise.resolve();
    // Hitting /discount/CODE sets the discount on the session/checkout. We
    // follow the redirect with fetch so no navigation happens.
    return fetch(`/discount/${encodeURIComponent(this.discountCode)}`, {
      method: 'GET',
      redirect: 'follow',
    }).catch((e) => console.error('Discount apply failed', e));
  }

  showErrorMessage(errorMessage = false) {
    this.errorMessageElement = this.errorMessageElement || this.querySelector('.product-form__error-message');
    if (this.errorMessageElement) {
      this.errorMessageElement.hidden = !errorMessage;
      if (errorMessage) this.errorMessageElement.textContent = errorMessage;
    } else {
      errorMessage && alert(errorMessage);
    }
  }

  toggleButtonLoading(status) {
    if (status) {
      this.addToCartButtonElement.classList.add('btn--loading');
      this.addToCartButtonElement.setAttribute('disabled', true);
    } else {
      this.addToCartButtonElement.classList.remove('btn--loading');
      this.addToCartButtonElement.removeAttribute('disabled');
    }
  }
}
customElements.define('go-products-bundle', GoProductsBundle);
